import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useDeleteProduct, useProductsQuery } from "../api/queries";
import { ProductsTour, useProductsTour } from "../tour";
import {
  getProductCatalogConfig,
  type ProductCatalogMode,
} from "../ui/product-catalog-config";
import { ProductsFilter } from "../ui/ProductsFilter";
import { ProductsTable } from "../ui/ProductsTable";
import { ProductsToolbar } from "../ui/ProductsToolbar";

type ProductsPageProps = {
  catalogMode?: ProductCatalogMode;
};

export function ProductsPage({ catalogMode = "retail" }: ProductsPageProps) {
  const config = getProductCatalogConfig(catalogMode);
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as OffsetPageQueryDTO & {
    search?: string;
    status?: "draft" | "active" | "archived";
  };
  const { run, handleJoyrideCallback, startTour } = useProductsTour();

  const list = useProductsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
    search: search.search,
    status: search.status,
    kind: config.listFilters.kind,
    isSellable: config.listFilters.isSellable,
  });

  const canManage = useActionPermission(["catalog:create"]);
  const deleteProduct = useDeleteProduct();

  const isShowcaseCatalog = catalogMode !== "retail";

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: isShowcaseCatalog ? "ລຶບແບບຕົວຢ່າງ" : "ລຶບສິນຄ້າ",
      description: isShowcaseCatalog
        ? "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບແບບນີ້?"
        : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບສິນຄ້ານີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteProduct.run(id), {
        loading: "ກຳລັງລຶບ...",
        success: isShowcaseCatalog
          ? "ລຶບແບບຕົວຢ່າງສຳເລັດ"
          : "ລຶບສິນຄ້າສຳເລັດ",
        error: "ລຶບລົ້ມເຫຼວ",
      });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: config.labels.breadcrumb },
          ]}
        />
      </Header>
      <Main>
        <ProductsToolbar
          config={config}
          canManage={!!canManage}
          onCreate={() => nav({ to: config.routes.new })}
          onStartTour={catalogMode === "retail" ? startTour : undefined}
        />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ProductsFilter />
          <ProductsTable
            config={config}
            canManage={!!canManage}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(row) =>
              nav({
                to: config.routes.detail,
                params: { id: row.id },
              })
            }
            onEdit={(row) =>
              nav({
                to: config.routes.edit,
                params: { id: row.id },
              })
            }
            onDelete={handleDelete}
          />
        </div>
        {catalogMode === "retail" && (
          <ProductsTour run={run} onCallback={handleJoyrideCallback} />
        )}
      </Main>
    </>
  );
}
